
import { PropType, defineComponent, computed } from 'vue';

import useNumbers, { FNumFormats } from '@/composables/useNumbers';
import { getAddress } from '@ethersproject/address';

import {
  BaseFlakeInfos,
  FarmUserInfo
} from '@/services/balancer/subgraph/types';

import { bnum } from '@/lib/utils';
import useTokens from '@/composables/useTokens';
import useWeb3 from '@/services/web3/useWeb3';
import useUserSettings from '@/composables/useUserSettings';
import useUserFlakeInfosQuery from '@/composables/queries/useUserFlakeInfosQuery';

export default defineComponent({
  props: {
    userInfos: { type: Object as PropType<FarmUserInfo[]> },
    baseFlakeInfos: { type: Object as PropType<BaseFlakeInfos[]> },
    loading: { type: Boolean, default: true },
    personal: { type: Boolean, default: false }
  },

  setup(props) {
    // COMPOSABLES
    const { fNum2 } = useNumbers();
    const { prices } = useTokens();
    const { appNetworkConfig } = useWeb3();
    const { currency } = useUserSettings();
    const { data: userFlakeInfosQuery } = useUserFlakeInfosQuery();

    // COMPUTED
    const pendingFlake = computed(() =>
      userFlakeInfosQuery.value && userFlakeInfosQuery.value[0]
        ? userFlakeInfosQuery.value[0].pendingFlake
        : ''
    );
    const veFlakeBalance = computed(() =>
      userFlakeInfosQuery.value && userFlakeInfosQuery.value[0]
        ? userFlakeInfosQuery.value[0].veFlakeBalance
        : ''
    );
    const boostStakedFlake = computed(() => {
      return props.userInfos && props.userInfos.length !== 0
        ? props.userInfos
            .reduce(
              (pre, cur) =>
                bnum(pre).plus(
                  bnum(cur.boostStaked || '0').dividedBy(Math.pow(10, 18))
                ),
              bnum(0)
            )
            .toFixed(0)
        : '0';
    });
    const totalStakedFlake = computed(() => {
      return props.baseFlakeInfos && props.baseFlakeInfos[0]
        ? bnum(props.baseFlakeInfos[0].totalStakedFlake || '0')
            .dividedBy(Math.pow(10, 18))
            .toFixed(0)
        : '0';
    });

    const rate = computed(() => {
      return props.baseFlakeInfos && props.baseFlakeInfos[0]
        ? bnum(props.baseFlakeInfos[0].rate || '0')
            .dividedBy(Math.pow(10, 18))
            .toFixed(0)
        : '0';
    });

    const flakePrice = computed(() => {
      if (prices.value && appNetworkConfig.addresses) {
        return (
          prices.value[getAddress(appNetworkConfig.addresses.flake || '')][
            currency.value
          ] || '0'
        );
      } else {
        return '0';
      }
    });

    const stats = computed(() => {
      if (!props.personal)
        return [
          {
            id: 'tvl',
            label: 'TVL',
            value: fNum2(
              bnum(flakePrice.value)
                .times(totalStakedFlake.value)
                .toFixed(0),
              FNumFormats.fiat
            )
          },
          {
            id: 'FLAKE for each veFlake',
            label: 'FLAKE for each veFlake',
            value: rate.value
          },
          {
            id: 'Locked FLAKE',
            label: 'Locked FLAKE',
            value: fNum2(totalStakedFlake.value)
          },
          {
            id: 'Basic APR',
            label: 'Basic APR',
            value: '-'
          }
        ];
      return [
        {
          id: 'Your veFlake amount',
          label: 'Your veFlake amount',
          value: fNum2(
            bnum(veFlakeBalance.value ? veFlakeBalance.value : '0')
              .dividedBy(Math.pow(10, 18))
              .toFixed(0)
          )
        },
        {
          id: 'Boost staked in farming ',
          label: 'Boost staked in farming ',
          value: fNum2(boostStakedFlake.value)
        },
        {
          id: 'Your pending amount',
          label: 'Your pending amount',
          value: fNum2(
            bnum(pendingFlake.value ? pendingFlake.value : '0')
              .dividedBy(Math.pow(10, 18))
              .toString()
          )
        }
      ];
    });

    return {
      stats
    };
  }
});
